// PATH: src/components/PhotoUpload.tsx — sélecteur de photo (élève).
//
// Envoie l'image au backend TilmiSchool (relais authentifié vers tc_upload) et
// remonte l'URL publique via onChange. Pensé pour un Form.Item : `value` =
// l'URL courante, `onChange` = la nouvelle URL, comme un contrôle antd normal.
import React, { useState } from "react";
import { Avatar, Button, Space, Upload, message } from "antd";
import { CameraOutlined, LoadingOutlined, UserOutlined } from "@ant-design/icons";
import { useSchoolApi } from "../api/school";

// Repli d'icône : une silhouette pour une personne. L'appelant peut fournir la
// sienne (un logo n'est pas une photo d'identité).
const DEFAULT_ICON = <UserOutlined />;

// Doit rester aligné sur la liste blanche du backend (hdl_school/upload.go).
const ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;

const PhotoUpload: React.FC<{
  value?: string;
  onChange?: (url: string) => void;
  // Réglages d'affichage : par défaut « photo » (élève, personnel), surchargés
  // pour d'autres images de même nature (ex. logo de l'établissement).
  addLabel?: string;
  changeLabel?: string;
  icon?: React.ReactNode;
}> = ({ value, onChange, addLabel = "Ajouter une photo", changeLabel = "Changer la photo", icon = DEFAULT_ICON }) => {
  const api = useSchoolApi();
  const [uploading, setUploading] = useState(false);

  // Contrôle côté client AVANT l'envoi : rejet immédiat, sans aller-retour
  // réseau. Le backend revalide de toute façon (le client ne protège rien).
  const beforeUpload = (file: File): boolean => {
    if (!ACCEPT.split(",").includes(file.type)) {
      message.error("Format non autorisé : JPEG, PNG ou WebP");
      return false;
    }
    if (file.size > MAX_BYTES) {
      message.error("Image trop lourde : 5 Mo maximum");
      return false;
    }
    setUploading(true);
    api.uploadPhoto(file)
      .then((url) => {
        onChange?.(url);
        // « Envoyée », surtout pas « mise à jour » : à cet instant l'image n'est
        // que dans le stockage. C'est l'enregistrement du formulaire qui la
        // rattache à la fiche — annoncer l'inverse a déjà fait perdre des photos.
        message.success("Photo envoyée — enregistrez la fiche pour l'attacher");
      })
      .catch((err) => message.error(err?.response?.data?.error || "Envoi impossible"))
      .finally(() => setUploading(false));
    // false : on gère l'envoi nous-mêmes, antd ne poste rien.
    return false;
  };

  return (
    <Space align="center" size={16}>
      <Avatar
        size={72}
        src={value || undefined}
        icon={uploading ? <LoadingOutlined /> : icon}
        shape="square"
      />
      <Upload accept={ACCEPT} showUploadList={false} beforeUpload={beforeUpload} maxCount={1}>
        <Button icon={<CameraOutlined />} loading={uploading}>
          {value ? changeLabel : addLabel}
        </Button>
      </Upload>
    </Space>
  );
};

export default PhotoUpload;
